import React from 'react';
import { ExternalLink } from 'lucide-react';
import { STORE_INFO } from '../constants';

type MenuItem = {
  name: string;
  price: number;
  spicy?: boolean;
  description?: string;
  photo?: string;
};

type Category = {
  name: string;
  items: MenuItem[];
};

const menu: Category[] = [
  {
    name: 'Appetizers',
    items: [
      { name: 'Chicken 65', price: 7.99, spicy: true, description: 'Spicy deep-fried chicken.' },
      { name: 'Paneer 65', price: 8.99 },
      { name: 'Pani Puri', price: 4.99 },
      { name: 'Pepper Chicken', price: 7.99 },
      { name: 'Chatpate', price: 4.99 },
      { name: 'Chilli Paneer', price: 8.99 },
      { name: 'Potato Sticks', price: 3.99 },
    ],
  },
  {
    name: 'Snacks',
    items: [
      { name: 'Samosa Veg', price: 3.49 },
      { name: 'Samosa Onion', price: 3.99 },
      { name: 'Punugulu', price: 3.99 },
      { name: 'Mirchi Bhaji', price: 3.99, spicy: true },
      { name: 'Onion Pakoda', price: 3.99 },
      { name: 'Kachori', price: 3.99 },
      { name: 'Vada Pav', price: 5.99 },
    ],
  },
  {
    name: 'Entree (with rice)',
    items: [
      { name: 'Butter Chicken', price: 11.99 },
      { name: 'Chicken Tikka Masala', price: 11.99 },
      { name: 'Paneer Butter Masala', price: 10.99 },
      { name: 'Paneer Tikka Masala', price: 10.99 },
      { name: 'Mutter Paneer', price: 10.99 },
    ],
  },
  {
    name: 'Chowmein',
    items: [
      { name: 'Chicken Chowmein', price: 10.99 },
      { name: 'Veg Chowmein', price: 9.99 },
    ],
  },
  {
    name: 'Momo',
    items: [
      { name: 'Chicken', price: 9.99 },
      { name: 'Chicken Fried', price: 10.99 },
      { name: 'Chicken Chilli', price: 11.99 },
      { name: 'Veg', price: 9.99 },
      { name: 'Veg Fried', price: 10.99 },
      { name: 'Veg Chilli', price: 11.99 },
    ],
  },
  {
    name: 'Fresh from Tandoor',
    items: [
      { name: 'Tandoori Chicken', price: 9.99 },
      { name: 'Chicken Tikka', price: 9.99 },
      { name: 'Paneer Tikka', price: 9.99 },
      { name: 'Chicken Sheesh Kabab', price: 9.99 },
    ],
  },
  {
    name: 'Rice/Biryanis',
    items: [
      { name: 'Chicken Biryani', price: 10.99 },
      { name: 'Chicken 65 Biryani', price: 10.99 },
      { name: 'Veg Biryani', price: 9.99 },
      { name: 'Flavored Plain Rice', price: 2.99 },
    ],
  },
  {
    name: 'Naan/Bread',
    items: [
      { name: 'Plain Naan', price: 2.49 },
      { name: 'Butter Naan', price: 2.99 },
      { name: 'Garlic Naan', price: 2.99 },
    ],
  },
  {
    name: 'Drinks',
    items: [
      { name: 'Mango Lassi', price: 3.99 },
      { name: 'Sweet Lassi', price: 2.99 },
      { name: 'Masala Tea', price: 1.29 },
    ],
  },
];

function MenuItem({ item }: { item: MenuItem }) {
  return (
    <div className="flex justify-between items-center py-2">
      <div className="flex items-center gap-2">
        {item.name} {item.spicy && <span className="text-red-500">🌶️</span>}
      </div>
      <div className="font-bold">${item.price.toFixed(2)}</div>
    </div>
  );
}

function Category({ category }: { category: Category }) {
  return (
    <div className="bg-white p-6 rounded-lg shadow">
      <h3 className="text-xl font-bold mb-4 text-[#568203]">{category.name}</h3>
      <div>
        {category.items.map((item, i) => (
          <MenuItem key={i} item={item} />
        ))}
      </div>
    </div>
  );
}

export default function CafeMenu() {
  return (
    <section className="py-12 bg-gray-50">
      <div className="container mx-auto px-4 text-center">
        {/* Heading */}
        <h2 className="text-3xl font-bold text-gray-800 mb-6">Basmati Cafe Menu</h2>
        <p className="text-gray-600 mb-8">
          Experience authentic Indian cuisine at our cafe. View our full menu and order online:
        </p>

        {/* Full Menu Link */}
        <a
          href={STORE_INFO.cafeUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 bg-[#568203] text-white px-6 py-3 rounded-lg hover:bg-[#476c02] transition-colors mb-8"
        >
          Order Now <ExternalLink size={20} />
        </a>

        {/* Menu Categories */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {menu.map((category, i) => (
            <Category key={i} category={category} />
          ))}
        </div>

        {/* Spicy Indicator */}
        <p className="text-sm text-gray-500 mt-6">🌶️ = Spicy</p>
      </div>
    </section>
  );
}
